import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { CameraCapture } from "@/components/CameraCapture";
import { PestReport } from "@/components/PestReport";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bug, Loader2, Languages, History, Trash2, MapPin, LocateFixed } from "lucide-react";
import { LANGUAGES } from "@/lib/languages";
import { toast } from "sonner";

export default function PestDoctor() {
  const { user, updateLanguage } = useAuth();
  const [language, setLanguage] = useState(user?.language || "English");
  const [photo, setPhoto] = useState(null);
  const [crop, setCrop] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [where, setWhere] = useState("none");
  const [gps, setGps] = useState(null);

  const changeLanguage = (l) => { setLanguage(l); updateLanguage(l); };
  const loadHistory = () => apiClient.get("/pest/reports").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    loadHistory();
    apiClient.get("/fields").then((r) => {
      const p = r.data.filter((f) => typeof f.lat === "number" && typeof f.lon === "number");
      setPinned(p);
      if (p[0]) setWhere(p[0].id);
    }).catch(() => {});
  }, []);

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("GPS not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGps({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setWhere("gps"); toast.success("Location captured"); },
      () => toast.error("Could not get your location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const locationPayload = () => {
    if (where === "gps" && gps) return { lat: gps.lat, lon: gps.lon };
    const f = pinned.find((x) => x.id === where);
    return f ? { lat: f.lat, lon: f.lon, field_id: f.id } : {};
  };

  const diagnose = async () => {
    if (!photo) return toast.error("Take or upload a photo of the plant first");
    setBusy(true);
    setReport(null);
    try {
      const res = await apiClient.post("/pest/diagnose", { image_base64: photo.base64, mime_type: photo.mime, crop, notes, language, ...locationPayload() });
      setReport(res.data);
      setHistory((h) => [res.data, ...h].slice(0, 10));
      toast.success("Diagnosis ready");
      setTimeout(() => document.getElementById("pest-anchor")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await apiClient.delete(`/pest/reports/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      if (report?.id === id) setReport(null);
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight mb-2">Pest &amp; Disease Doctor</h1>
        <p className="text-stone-600 mb-6">Photograph a sick leaf, fruit or a bug — get the likely problem and how to treat it, in your language.</p>

        <div className="flex items-center gap-3 mb-8 bg-white border border-stone-200 rounded-xl px-4 py-3 w-fit">
          <Languages className="h-4 w-4 text-[#2D6A4F]" />
          <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Advice language</span>
          <Select value={language} onValueChange={changeLanguage}>
            <SelectTrigger data-testid="pest-language-select" className="w-[160px] h-9 bg-white border-stone-200"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 mb-8" data-testid="pest-form">
          <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Photo of the problem</Label>
          <p className="text-sm text-stone-500 mb-4">Get close and fill the frame with the damaged leaf, spot or insect.</p>
          <CameraCapture photo={photo} onPhoto={setPhoto} />
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Crop (optional)</Label>
              <Input data-testid="pest-crop-input" value={crop} onChange={(e) => setCrop(e.target.value)} placeholder="e.g. Tomato" className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">What you noticed (optional)</Label>
              <Input data-testid="pest-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. yellow spots spreading for a week" className="mt-1.5 bg-white" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Where is this plant? (helps neighbours see what's spreading)</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
              <Select value={where} onValueChange={setWhere}>
                <SelectTrigger data-testid="pest-where-select" className="bg-white sm:w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pinned.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}{f.location ? ` · ${f.location}` : ""}</SelectItem>)}
                  {gps && <SelectItem value="gps">My GPS location ({gps.lat.toFixed(3)}, {gps.lon.toFixed(3)})</SelectItem>}
                  <SelectItem value="none">Don't record location</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="pest-gps-btn" type="button" variant="outline" onClick={useGps} className="border-stone-300"><LocateFixed className="h-4 w-4 mr-2" /> Use my GPS</Button>
            </div>
          </div>
          <Button data-testid="pest-diagnose-btn" onClick={diagnose} disabled={busy || !photo}
            className="mt-6 h-12 px-8 rounded-xl bg-[#D97706] hover:bg-[#78350F] text-white text-base font-semibold">
            {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Examining the plant…</> : <><Bug className="h-5 w-5 mr-2" /> Diagnose</>}
          </Button>
        </div>

        <div id="pest-anchor" />
        {report && <PestReport report={report} />}

        {history.length > 0 && (
          <div className="mt-10" data-testid="pest-history">
            <h2 className="font-display text-xl font-bold text-emerald-900 mb-4 flex items-center gap-2"><History className="h-5 w-5" /> Recent diagnoses</h2>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} data-testid={`pest-history-${h.id}`} className="bg-white rounded-xl border border-stone-200 px-4 py-3 flex items-center justify-between gap-3">
                  <button onClick={() => { setReport(h); setTimeout(() => document.getElementById("pest-anchor")?.scrollIntoView({ behavior: "smooth" }), 50); }} className="text-left min-w-0 flex-1">
                    <div className="font-semibold text-emerald-950 truncate">{h.result?.problem || "Diagnosis"}{h.crop ? ` · ${h.crop}` : ""}</div>
                    <div className="text-xs text-stone-500">{new Date(h.created_at).toLocaleDateString()} · {h.result?.type} · {h.result?.severity} severity</div>
                  </button>
                  <button data-testid={`delete-pest-${h.id}`} onClick={() => del(h.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
